import { RootState } from "app/services/store";
import { useSelector } from "react-redux";
import { View } from "ui/atoms/view";
import { Pressable, Text } from "react-native";
import {
  AlertCircleIcon,
  CheckCircleIcon,
  Icon,
  RepeatIcon,
} from "ui/gluestack/icon";
import { useEffect, useRef, useState } from "react";
import { useShowSyncProgress } from "../../offline/hooks/useShowSyncProgress";
import { SyncProgressModal } from "../../offline/modals/SyncProgressModal";

export function NetworkStatusBanner() {
  const wasNetworkReachable = useRef<boolean>(undefined);
  const hideBannerTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const networkState = useSelector(
    (state: RootState) => state.network?.networkState,
  );

  const [showBanner, setShowBanner] = useState(false);
  const isSyncingLoading = useSelector(
    (state: RootState) => state.network?.isSyncingLoading,
  );
  const {
    isSyncProgressModalVisible,
    showSyncProgressModal,
    hideSyncProgressModal,
    data,
    isLoading,
  } = useShowSyncProgress();
  const [showSyncComplete, setShowSyncComplete] = useState(false);
  const wasSyncing = useRef(false);

  useEffect(() => {
    if (networkState?.isInternetReachable) {
      hideBannerTimeout.current = setTimeout(() => {
        setShowBanner(!networkState.isInternetReachable);
      }, 3000);
    } else if (wasNetworkReachable.current !== undefined) {
      if (hideBannerTimeout.current) {
        clearTimeout(hideBannerTimeout.current);
      }
      setShowBanner(true);
    }

    wasNetworkReachable.current = networkState?.isInternetReachable;
  }, [networkState?.isInternetReachable]);

  useEffect(() => {
    if (!isSyncingLoading && wasSyncing.current) {
      setShowSyncComplete(true);
    }
    wasSyncing.current = isSyncingLoading ?? false;
  }, [isSyncingLoading]);

  const shouldShowBanner = showBanner || isSyncingLoading || showSyncComplete;

  if (!networkState || !shouldShowBanner) return;

  return (
    <View
      className={`w-full py-2 flex-row items-center justify-center gap-2 ${isSyncingLoading ? "bg-warning-100" : showSyncComplete ? "bg-green-500" : networkState.isInternetReachable ? "bg-green-500" : "bg-warning-100"}`}
    >
      <Icon
        as={
          isSyncingLoading
            ? RepeatIcon
            : showSyncComplete
              ? CheckCircleIcon
              : networkState.isInternetReachable
                ? CheckCircleIcon
                : AlertCircleIcon
        }
        className={`w-4 h-4 ${isSyncingLoading || !networkState.isInternetReachable ? "stroke-warning-800" : "stroke-green-950"}`}
      />
      <Text
        className={`${isSyncingLoading || !networkState.isInternetReachable ? "text-warning-800" : "text-green-950"} font-semibold`}
      >
        {isSyncingLoading
          ? "Syncing in progress"
          : showSyncComplete
            ? "Syncing complete"
            : networkState.isInternetReachable
              ? "Connection established"
              : "There seems to be an issue with your internet"}
      </Text>

      {(isSyncingLoading || showSyncComplete) && (
        <Pressable onPress={showSyncProgressModal}>
          <Text className="pl-3 text-typography-900 font-medium">View</Text>
        </Pressable>
      )}

      {showSyncComplete && (
        <Pressable onPress={() => setShowSyncComplete(false)}>
          <Text className="pl-3 text-typography-900 font-medium">Close</Text>
        </Pressable>
      )}

      <SyncProgressModal
        isVisible={isSyncProgressModalVisible}
        onClose={hideSyncProgressModal}
        title="Sync Progress"
        data={data}
        isLoading={isLoading}
      />
    </View>
  );
}
